<template>
  <div class="pdf-viewer">
    <header class="pdf-viewer__header box-shadow">
      <!-- <div class="pdf-preview-toggle">
        <a @click.prevent.stop="togglePreview" class="icon"><PreviewIcon /></a>
      </div> -->

      <PDFZoom
        :scale="scale"
        @change="updateScale"
        @fit="updateFit"
        class="header-item"
        />

      <PDFPaginator
        v-model="currentPage"
        :pageCount="pageCount"
        class="header-item"
        />

      <slot name="header"></slot>
    </header>
    <v-progress-linear
      indeterminate
      height="4"
      color="blue darken-2"
      v-if="lpage"
    ></v-progress-linear>
    <PDFData
      class="pdf-viewer__main"
      :url="url"
      @page-count="updatePageCount"
      @page-focus="updateCurrentPage"
      @document-rendered="onDocumentRendered"
      @document-errored="onDocumentErrored"
      >
      <template v-slot:preview="{pages}">
        <PDFPreview
          v-show="isPreviewEnabled"
          class="pdf-viewer__preview"
          v-bind="{pages, scale, currentPage, pageCount, isPreviewEnabled}"
          />
      </template>
      <template v-slot:document="{pages}">
        <PDFDocument
          class="pdf-viewer__document"
          :class="{ 'preview-enabled': isPreviewEnabled }"
          ref="pdfDoc"
          v-bind="{pages, scale, optimalScale, fit, currentPage, pageCount, isPreviewEnabled, handleSelection}"
          @scale-change="updateScale"
          />
      </template>
    </PDFData>
    <div v-if="showPopup&&allowSelection" class="popup-overlay">
        <div class="popup">
          <p class="modal-text">
            Are you sure you want to mark this document as {{selectedSelectionInfo.code.label}}?
          </p>
          <div class="btn-container">
            <v-btn color="seconday" label @click="handleCodeSelectionCancel" class="modal-btn">
              Cancel
            </v-btn>
            <v-btn color="primary" label @click="handleConfirmSelection" :disabled="loading" class="modal-btn">
             {{loading? 'Saving...': 'Save'}}
            </v-btn>
          </div>
        </div>
      </div>
  </div>
</template>

<script>
// import PreviewIcon from '../assets/icon-preview.svg'

import PDFDocument from './PDFDocument'
import PDFData from './PDFData'
import PDFPaginator from './PDFPaginator'
import PDFPreview from './PDFPreview'
import PDFZoom from './PDFZoom'
import SelectionBox from '../components/PDFSelection'
import { DOCUMENT_SELECTION_EVENTS } from '../utils/constants'
import { transformSelections } from '../utils/helpers'

const { getValidSelections } = DOCUMENT_SELECTION_EVENTS
function floor (value, precision) {
  const multiplier = Math.pow(10, precision || 0)
  return Math.floor(value * multiplier) / multiplier
}

export default {
  name: 'PDFViewer',

  components: {
    PDFDocument,
    PDFData,
    PDFPaginator,
    PDFPreview,
    PDFZoom
    // PreviewIcon
  },

  props: {
    allowSelection: Boolean,
    existingSelections: Array,
    currentSelections: Array,
    url: String,
    reasonOptions: {
      type: Array,
      default: () => []
    },
    handleSaveSelections: {
      type: Function,
      default: () => {}
    }
  },

  data () {
    return {
      scale: undefined,
      optimalScale: undefined,
      fit: undefined,
      currentPage: 1,
      pageCount: undefined,
      isPreviewEnabled: false,
      lpage: true,
      tempSelection: null,
      isDrawing: false,
      selectionRects: this.existingSelections,
      showPopup: false,
      selectedSelectionInfo: null,
      loading: false
    }
  },

  methods: {
    onDocumentRendered () {
      this.$emit('document-loaded', this.url)
      this.selectionRects.forEach(this.renderSelectionToPage)
    },

    onDocumentErrored (e) {
      this.lpage = false
      this.$emit('document-errored', e)
    },

    updateScale ({ scale, isOptimal = false }) {
      this.lpage = false
      this.$emit('document-loaded', this.url)
      const roundedScale = floor(scale, 2)
      if (isOptimal) this.optimalScale = roundedScale
      this.scale = roundedScale
      this.$emit('onDocumentRendered', this.url)
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.redrawAllSelections()
        }, 400)
      })
    },

    redrawAllSelections () {
      // First, remove all existing selection rectangles
      const allSelections = this.$refs.pdfDoc.$el.querySelectorAll('.selection-container')
      allSelections.forEach(el => el.remove())

      // Re-render all saved selections
      this.selectionRects.forEach(rect => {
        this.renderSelectionToPage(rect)
      })
    },
    updateFit (fit) {
      this.fit = fit
    },

    updatePageCount (pageCount) {
      this.pageCount = pageCount
    },

    updateCurrentPage (pageNumber) {
      this.currentPage = pageNumber
    },

    togglePreview () {
      this.isPreviewEnabled = !this.isPreviewEnabled
    },

    handleSelection (event, type) {
      if (this.allowSelection) {
        switch (type) {
          case 'mouseDown': {
            this.startSelection(event)
            break
          }
          case 'mouseMove': {
            this.updateSelection(event)
            break
          }
          case 'mouseUp': {
            this.endSelection(event)
            break
          }
          default:
        }
      }
    },
    startSelection (e) {
      const container = e.currentTarget
      const rect = container.getBoundingClientRect()

      const containerWidth = rect.width
      const containerHeight = rect.height

      const offsetX = e.clientX - rect.left
      const offsetY = e.clientY - rect.top

      this.tempSelection = {
        left: offsetX / containerWidth,
        top: offsetY / containerHeight,
        pdf_page_number: this.currentPage
      }

      this.isDrawing = true
    },

    updateSelection (e) {
      if (!this.isDrawing || !this.tempSelection) return

      const container = e.currentTarget
      const rect = container.getBoundingClientRect()

      const containerWidth = rect.width
      const containerHeight = rect.height

      const offsetX = e.clientX - rect.left
      const offsetY = e.clientY - rect.top

      const currentX = offsetX / containerWidth
      const currentY = offsetY / containerHeight

      const { left, top, pdf_page_number: pageNo } = this.tempSelection

      const leftUpdated = Math.min(currentX, left)
      const topUpdated = Math.min(currentY, top)
      const width = Math.abs(currentX - left)
      const height = Math.abs(currentY - top)

      const updatedSelection = {
        pdf_page_number: pageNo,
        left: leftUpdated,
        top: topUpdated,
        width,
        height,
        reason: ''
      }

      this.tempSelection = updatedSelection

      this.removeTempRectangle()
      this.renderSelectionToPage(updatedSelection, true)
    },

    endSelection () {
      if (this.tempSelection) {
        const { width, height, pdf_page_number: pageNo } = this.tempSelection
        const pageEl = this.$refs.pdfDoc.$el.querySelector(
          `[data-page-number="${pageNo}"]`
        )
        if (!pageEl) return

        const containerRect = pageEl.getBoundingClientRect()
        const containerWidth = containerRect.width
        const containerHeight = containerRect.height

        const widthPx = width * containerWidth
        const heightPx = height * containerHeight

        // Ignore selections smaller than 10px by 10px
        if (!width || !height || widthPx < 20 || heightPx < 20) {
          this.tempSelection = null
          this.removeTempRectangle()
          this.isDrawing = false
          return
        }
        const finalSelection = { ...this.tempSelection }
        this.selectionRects.push(finalSelection)
        this.removeTempRectangle()
        this.renderSelectionToPage(finalSelection, false)
      }
      this.tempSelection = null
      this.isDrawing = false
    },

    getRectangleStyle (rect) {
      return {
        top: rect.top + 'px',
        left: rect.left + 'px',
        width: rect.width + 'px',
        height: rect.height + 'px'
      }
    },
    renderSelectionToPage (rect, isTemp = false) {
      const pageEl = this.$refs.pdfDoc.$el.querySelector(
        `[data-page-number="${rect.pdf_page_number}"]`
      )
      if (!pageEl) return

      const containerRect = pageEl.getBoundingClientRect()
      const containerWidth = containerRect.width
      const containerHeight = containerRect.height

      const selectionData = {
        topPx: rect.top * containerHeight,
        leftPx: rect.left * containerWidth,
        widthPx: rect.width * containerWidth,
        heightPx: rect.height * containerHeight,
        error: rect.error,
        reason: rect.reason
      }

      const mountPoint = document.createElement('div')
      pageEl.appendChild(mountPoint)
      new this.$root.constructor({
        render: h =>
          h(SelectionBox, {
            props: {
              rect: selectionData,
              onChange: (data) => this.onReasonChange(data, rect),
              isTemp: isTemp,
              onRemove: (event) => this.removeSelection(event, rect),
              reasonOptions: this.reasonOptions,
              handleCodeChange: (selectedCode) => { this.handleCodeSelection(selectedCode, rect) }
            }
          })
      }).$mount(mountPoint)
    },
    removeTempRectangle () {
      const existing = this.$refs.pdfDoc.$el.querySelector(
        '.selection-container.temp'
      )
      if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing)
      }
    },
    findSelectionIndex (rect) {
      return this.selectionRects.findIndex(item => {
        const { top, left, width, height } = item
        const { top: rectTop, left: RectLeft, width: rectWidth, height: rectHeight } = rect
        return top === rectTop && left === RectLeft && width === rectWidth && height === rectHeight
      })
    },
    onReasonChange (data, rect) {
      const index = this.findSelectionIndex(rect)
      const existingSelections = [...this.selectionRects]

      if (index > -1) {
        existingSelections[index].reason = data.reason
        existingSelections[index].piq_code = data.piq_code
      }
      this.selectionRects = existingSelections
    },
    removeSelection (event, rect) {
      const index = this.findSelectionIndex(rect)
      if (index > -1) {
        this.selectionRects.splice(index, 1)
      }
      this.selectionRects = this.selectionRects.filter(item => {
        const { top, left, width, height } = item
        const { top: rectTop, left: RectLeft, width: rectWidth, height: rectHeight } = rect
        return !(top === rectTop && left === RectLeft && width === rectWidth && height === rectHeight)
      })
      const box = event.currentTarget.closest('.selection-container')
      if (box && box.parentNode) {
        box.parentNode.removeChild(box)
      }
    },
    captureScreenshot (pageNo, rect) {
      const canvas = this.$refs.pdfDoc.$el.querySelector(
        `[data-page-number="${pageNo}"] canvas`
      )
      if (!canvas) return null

      const canvasRect = canvas.getBoundingClientRect()
      const scaleX = canvas.width / canvasRect.width
      const scaleY = canvas.height / canvasRect.height

      const sx = rect.left * canvasRect.width * scaleX
      const sy = rect.top * canvasRect.height * scaleY
      const sw = rect.width * canvasRect.width * scaleX
      const sh = rect.height * canvasRect.height * scaleY

      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = sw
      tempCanvas.height = sh
      const ctx = tempCanvas.getContext('2d')

      ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh)
      const dataURL = tempCanvas.toDataURL('image/png')
      return dataURL
    },
    validateAndShareSelectionsToParent (callback) {
      if (this.selectionRects.length > 0) {
        const updatedSelections = this.selectionRects.map(rect => {
          const error = rect.error || {}
          if (!rect.reason) {
            error.hasError = true
            error.errorMessage = 'Reason is required'
          }

          return {
            ...rect,
            error
          }
        })
        this.selectionRects = [...updatedSelections]
        const areAllSelectionsValid = this.selectionRects.every(rectangle => !rectangle.error.hasError)

        let codeToSelect = null
        if (this.selectedSelectionInfo) {
          codeToSelect = this.selectedSelectionInfo.code && this.selectedSelectionInfo.code.value
        }
        if (areAllSelectionsValid) {
          const updatedSelections = transformSelections(this.selectionRects, this.captureScreenshot)
          callback(updatedSelections, codeToSelect)
        } else {
          this.redrawAllSelections()
        }
      }
    },
    handleConfirmSelection () {
      this.loading = true
      this.handleSaveSelections(this.selectionRects, this.selectedSelectionInfo.code.value).then(() => {
        this.showPopup = false
        this.loading = false
      })
    },
    handleCodeSelectionCancel () {
      this.showPopup = false
      this.redrawAllSelections()
      this.selectedSelectionInfo = null
    },
    handleCodeSelection (data, rect) {
      this.selectedSelectionInfo = {
        selection: rect,
        code: data
      }
      this.showPopup = true
    }
  },

  watch: {
    url () {
      this.currentPage = undefined
    },
    existingSelections: {
      immediate: true,
      handler () {
        this.selectionRects = this.existingSelections
        this.redrawAllSelections()
      }
    }
  },

  mounted () {
    document.body.classList.add('overflow-hidden')
  },
  destroyed () {
    document.body.classList.remove('overflow-hidden')
  },
  created () {
    this.$on(getValidSelections, (callback) => {
      this.validateAndShareSelectionsToParent(callback)
    })
  }
}
</script>

<style scoped>
header {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 1em;
  position: relative;
  z-index: 99;
}
.header-item {
  margin: 0 2.5em;
}

.pdf-viewer .pdf-viewer__document,
.pdf-viewer .pdf-viewer__preview {
  top: 70px;
}

.pdf-viewer__preview {
  display: block;
  width: 15%;
  right: 85%;
}

.pdf-viewer__document {
  top: 70px;
  width: 100%;
  left: 0;
}

.pdf-viewer__document.preview-enabled {
  width: 85%;
  left: 15%;
}

.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.popup {
  background: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
.btn-container{
  display: flex;
  justify-content: flex-end;
  gap:1rem;
}
.modal-btn {
  font-size: 0.75rem;
  border-radius: 0.25rem;
}
.modal-text{
  font-size: 1rem;
}
@media print {
  header {
    display: none;
  }
}
</style>
